// components/agent/ActiveTerritoriesModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin } from 'react-feather';
import AuthService from '../../services/authService';

const ActiveTerritoriesModal = ({ isOpen, onClose }) => {
  const [pools, setPools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    if (isOpen) {
      fetchMyPools();
    }
  }, [isOpen]);

  const fetchMyPools = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/agent-pools/my-pools`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch territories');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setPools(data.data);
      } else {
        setPools([]);
      }
    } catch (error) {
      console.error('Error fetching territories:', error);
      setError('Failed to load territories. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: '20px'
    }}
    onClick={onClose}
    >
      <div 
        className="active-territories-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '420px',
          maxHeight: '80vh',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)'
        }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '18px 22px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MapPin size={18} style={{ color: '#111' }} />
            <h2 style={{
              margin: 0,
              fontSize: '1.05rem',
              fontWeight: '700',
              color: '#111',
              letterSpacing: '-0.01em'
            }}>
              Active Territories
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.12s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            <X size={16} style={{ color: '#6b7280' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px 22px 22px',
          overflowY: 'auto',
          maxHeight: 'calc(80vh - 70px)'
        }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '2.5px solid #e5e7eb',
                borderTopColor: '#111',
                borderRadius: '50%',
                animation: 'spin 0.9s linear infinite'
              }} />
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : error ? (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '9px',
              padding: '11px 14px',
              color: '#b91c1c',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {error}
            </div>
          ) : pools.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '30px 20px',
              color: '#6b7280'
            }}>
              <MapPin size={36} style={{ 
                color: '#d1d5db',
                marginBottom: '12px'
              }} />
              <p style={{ fontSize: '0.9rem', margin: 0 }}>
                No active territories assigned.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[...pools]
                .sort((a, b) => {
                  const aText = a.serviceArea?.city && a.serviceArea?.state 
                    ? `${a.serviceArea.city}, ${a.serviceArea.state}` 
                    : a.name;
                  const bText = b.serviceArea?.city && b.serviceArea?.state 
                    ? `${b.serviceArea.city}, ${b.serviceArea.state}` 
                    : b.name;
                  return aText.localeCompare(bText);
                })
                .map((pool) => {
                const city = pool.serviceArea?.city;
                const state = pool.serviceArea?.state;
                const displayText = city && state 
                  ? `${city}, ${state}` 
                  : pool.name;
                
                return (
                  <div
                    key={pool._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '11px 14px',
                      backgroundColor: '#fafafa',
                      border: '1px solid #f0f0f0',
                      borderRadius: '10px'
                    }}
                  >
                    <MapPin size={15} style={{ color: '#6b7280', flexShrink: 0 }} />
                    <span style={{
                      color: '#111',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      {displayText}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Helper text */}
          {!isLoading && !error && (
            <p style={{
              margin: '16px 0 0 0',
              fontSize: '0.76rem',
              color: '#9ca3af',
              textAlign: 'center'
            }}>
              To update your territories, use the Get Support button.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActiveTerritoriesModal;